import { useParams, useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Edit,
  Plus,
  Calendar,
  Loader2,
  DollarSign,
  ArrowLeft,
  Trash2,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { StatusBadge } from '@/components/StatusBadge';
import { format } from 'date-fns';

interface QuoteLineItem {
  id: string;
  lineNumber: number;
  productId?: string;
  productName?: string;
  productCode?: string;
  descriptionOverride?: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  unitCost?: string;
  priceSource: string;
  discountPct: string;
  discountAmount: string;
  extendedAmount: string;
  taxAmount: string;
  lineMarginAmount: string;
  lineMarginPercent: string;
  notes?: string;
}

interface Quote {
  id: string;
  quoteNumber: string;
  accountId: string;
  quoteDate: string;
  validUntil: string;
  currencyCode: string;
  paymentTerms?: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  shippingAmount: string;
  total: string;
  totalCost: string;
  marginAmount: string;
  marginPercent: string;
  status: string;
  convertedToOrderId?: string;
  convertedAt?: string;
  salesRepId?: string;
  approvedBy?: string;
  approvedAt?: string;
  notes?: string;
  internalNotes?: string;
  createdAt: string;
  updatedAt: string;
  account?: {
    name: string;
    code?: string;
  };
  lineItems: QuoteLineItem[];
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: quoteData, isLoading } = useQuery({
    queryKey: ['quote', id, sessionId],
    queryFn: async () => {
      const result = await api.get<{ quote: Quote }>(`/quotes/${id}`, sessionId);
      return result.quote;
    },
    enabled: !!sessionId && !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/quotes/${id}`, sessionId);
    },
    onSuccess: () => {
      toast({
        title: "Quote deleted",
        description: "The quote has been archived successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      setLocation('/quotes');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete quote",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[300px] w-full rounded-lg" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!quoteData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Quote not found</p>
      </div>
    );
  }

  const quote = quoteData;

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation('/quotes')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{quote.quoteNumber}</h1>
            {quote.account && (
              <p className="text-sm text-muted-foreground mt-1">
                {quote.account.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={quote.status} type="quote" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending || quote.status === 'CONVERTED'}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Archive
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote Details */}
          <Card>
            <CardHeader className="border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <CardTitle>Quote Details</CardTitle>
                </div>
                <Button variant="ghost" size="icon">
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Quote Number</p>
                  <p className="font-medium">{quote.quoteNumber}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Quote Date</p>
                  <p>{format(new Date(quote.quoteDate), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Valid Until</p>
                  <p>{format(new Date(quote.validUntil), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Payment Terms</p>
                  <p>{quote.paymentTerms || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Currency</p>
                  <p>{quote.currencyCode}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={quote.status} type="quote" />
                </div>
              </div>
              {quote.notes && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
                </div>
              )}
              {quote.internalNotes && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Internal Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{quote.internalNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-green-500" />
                  <CardTitle>Line Items</CardTitle>
                </div>
                <Button variant="ghost" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {quote.lineItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Package className="h-10 w-10 mb-2 opacity-50" />
                  <p className="text-sm font-medium">No line items</p>
                  <p className="text-xs mt-1">Add products or services to this quote</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Discount</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quote.lineItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.lineNumber}</TableCell>
                          <TableCell>
                            <div>
                              {item.productName && (
                                <div className="font-medium">{item.productName}</div>
                              )}
                              {item.productCode && (
                                <div className="text-xs text-muted-foreground">{item.productCode}</div>
                              )}
                              {item.descriptionOverride && (
                                <div className="text-sm">{item.descriptionOverride}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {Number(item.quantity).toLocaleString()} {item.uom}
                          </TableCell>
                          <TableCell className="text-right">
                            ${Number(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right">
                            {Number(item.discountPct) > 0 ? (
                              <span className="text-orange-600">
                                {Number(item.discountPct).toFixed(1)}%
                              </span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ${Number(item.extendedAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={Number(item.lineMarginPercent) < 0 ? 'text-red-600' : 'text-green-600'}>
                              {Number(item.lineMarginPercent).toFixed(1)}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column (1/3 width) */}
        <div className="space-y-6">
          {/* Financial Summary */}
          <Card>
            <CardHeader className="border-l-4 border-yellow-500">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-500" />
                <CardTitle>Financial Summary</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium">
                  ${Number(quote.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {Number(quote.discountAmount) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className="text-orange-600">
                    -${Number(quote.discountAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {Number(quote.taxAmount) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="font-medium">
                    ${Number(quote.taxAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {Number(quote.shippingAmount) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">
                    ${Number(quote.shippingAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-base font-semibold pt-3 border-t">
                <span>Total</span>
                <span>
                  ${Number(quote.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="pt-3 border-t space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Cost</span>
                  <span className="font-medium">
                    ${Number(quote.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Margin</span>
                  <span className={`font-semibold ${Number(quote.marginPercent) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${Number(quote.marginAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}
                    ({Number(quote.marginPercent).toFixed(1)}%)
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader className="border-l-4 border-slate-500">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-500" />
                <CardTitle>Metadata</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="font-medium">{format(new Date(quote.createdAt), 'MMM d, yyyy h:mm a')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Last Updated</p>
                <p className="font-medium">{format(new Date(quote.updatedAt), 'MMM d, yyyy h:mm a')}</p>
              </div>
              {quote.convertedAt && (
                <div>
                  <p className="text-xs text-muted-foreground">Converted</p>
                  <p className="font-medium">{format(new Date(quote.convertedAt), 'MMM d, yyyy h:mm a')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
